import { ApiError } from '../api/ApiError';
import { apiFetch, type RequestOptions } from '../api/client';
import { refresh } from './authApi';
import { currentAccessToken, useSessionStore } from './sessionStore';

/**
 * In-flight refresh, shared by every caller.
 *
 * **This single-flight guard is load-bearing, not an optimisation.** The API rotates
 * refresh tokens and treats reuse of a rotated one as theft — revoking the entire chain
 * and signing the owner out. If three queries 401 at once and each calls refresh
 * independently, two of them present a token the first has already rotated. Inside the
 * server's 30-second grace window that is tolerated; outside it, the session is destroyed.
 *
 * Sharing one promise means concurrent 401s produce exactly one refresh.
 */
let inFlightRefresh: Promise<string> | null = null;

async function refreshOnce(): Promise<string> {
  inFlightRefresh ??= refresh()
    .then((response) => {
      useSessionStore.getState().setSession({
        accessToken: response.accessToken,
        email: response.email,
        mustChangePassword: response.mustChangePassword,
      });

      return response.accessToken;
    })
    .catch((error: unknown) => {
      // The refresh cookie is gone or rejected: the session is genuinely over.
      useSessionStore.getState().setSession(null);
      throw error;
    })
    .finally(() => {
      inFlightRefresh = null;
    });

  return inFlightRefresh;
}

/**
 * Calls the API with the current access token, refreshing once on a 401.
 *
 * Retries at most once. A second 401 after a successful refresh means something other
 * than expiry is wrong, and retrying again would loop.
 */
export async function authenticatedFetch<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const token = currentAccessToken();

  try {
    return await apiFetch<T>(path, withBearer(options, token));
  } catch (error) {
    if (!(error instanceof ApiError) || !error.isUnauthorized) {
      throw error;
    }

    const refreshed = await refreshOnce();

    return apiFetch<T>(path, withBearer(options, refreshed));
  }
}

/** Attempts to restore a session from the refresh cookie. Used once, on boot. */
export async function restoreSession(): Promise<void> {
  const store = useSessionStore.getState();

  try {
    await refreshOnce();
  } catch {
    // No session to restore. Not an error — this is the ordinary anonymous case, and
    // the public site must not show an error because nobody is signed in.
    store.setSession(null);
  } finally {
    store.setRestored();
  }
}

function withBearer(options: RequestOptions, token: string | null): RequestOptions {
  if (token === null) {
    return options;
  }

  return {
    ...options,
    headers: { ...options.headers, Authorization: `Bearer ${token}` },
  };
}
